/**
 * The console's URL is its whole address: cluster, namespace, resource kind,
 * open object, active tab, filters, and sort all live in the query string.
 * Anything on screen can therefore be linked to, and any link restores what
 * the sender was looking at.
 *
 * Defaults are omitted when serializing, so an ordinary link stays short and
 * a bare `/kubernetes` is still valid.
 */

export type StatusBucketId = 'healthy' | 'warning' | 'error' | 'unknown';
export type SortDirection = 'asc' | 'desc';

export interface ConsoleUrlState {
  cluster: string;
  namespace: string;
  /** Kind id, or one of the standalone views: overview / topology / operations. */
  section: string;
  /** Name of the object whose detail drawer is open; empty when none is. */
  name: string;
  /** Detail drawer tab; empty means the drawer's own default. */
  tab: string;
  /** Free-text name filter. */
  q: string;
  /** Label selector, as typed. */
  labels: string;
  status: StatusBucketId[];
  /** Column label the list is sorted by. */
  sort: string;
  dir: SortDirection;
  /** Operations centre tab. */
  ops: string;
}

export const DEFAULT_SECTION = 'overview';
export const DEFAULT_SORT = 'Name';
export const DEFAULT_DIR: SortDirection = 'asc';
export const DEFAULT_OPS_TAB = 'promql';

export const STANDALONE_SECTIONS = ['overview', 'topology', 'operations'];
export const OPS_TABS = ['promql', 'alerts', 'dashboard', 'quickstarts'];

const STATUS_IDS: StatusBucketId[] = [
  'healthy',
  'warning',
  'error',
  'unknown',
];

export const EMPTY_URL_STATE: ConsoleUrlState = {
  cluster: '',
  namespace: '',
  section: DEFAULT_SECTION,
  name: '',
  tab: '',
  q: '',
  labels: '',
  status: [],
  sort: DEFAULT_SORT,
  dir: DEFAULT_DIR,
  ops: DEFAULT_OPS_TAB,
};

/**
 * Changing one of these means the user went somewhere else, so it earns a
 * history entry. Everything else — typing in the filter box, flipping a sort
 * — replaces the current entry instead, to keep Back useful.
 */
export const CONSOLE_NAVIGATION_KEYS = [
  'cluster',
  'namespace',
  'section',
  'name',
  'tab',
  'ops',
] as const satisfies readonly (keyof ConsoleUrlState)[];

/**
 * Reads console state out of a query string.
 *
 * `resolveSection` maps a legacy `kind=Pod` parameter onto a kind id, and
 * validates `section`; it returns null for anything unrecognised, which falls
 * back to the overview rather than rendering an empty list for a kind that
 * does not exist.
 */
export function parseConsoleUrl(
  params: URLSearchParams,
  resolveSection: (candidate: string) => string | null,
): ConsoleUrlState {
  const rawSection =
    params.get('section') ??
    params.get('resource') ??
    params.get('kind') ??
    '';
  const section = rawSection ? resolveSection(rawSection) : null;

  const dir = params.get('dir');
  const ops = params.get('ops');

  return {
    cluster: params.get('cluster') ?? '',
    namespace: params.get('namespace') ?? '',
    section: section ?? DEFAULT_SECTION,
    name: params.get('name') ?? '',
    tab: params.get('tab') ?? '',
    q: params.get('q') ?? '',
    labels: params.get('labels') ?? '',
    status: (params.get('status') ?? '')
      .split(',')
      .filter((value): value is StatusBucketId =>
        STATUS_IDS.includes(value as StatusBucketId),
      ),
    sort: params.get('sort') || DEFAULT_SORT,
    dir: dir === 'desc' ? 'desc' : DEFAULT_DIR,
    ops: ops && OPS_TABS.includes(ops) ? ops : DEFAULT_OPS_TAB,
  };
}

/** Writes console state as a query string, omitting everything at default. */
export function serializeConsoleUrl(state: ConsoleUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, value: string) => {
    if (value) params.set(key, value);
  };

  set('cluster', state.cluster);
  set('namespace', state.namespace);
  if (state.section && state.section !== DEFAULT_SECTION) {
    params.set('section', state.section);
  }
  set('name', state.name);
  // A tab without an open object addresses nothing.
  if (state.name) set('tab', state.tab);
  set('q', state.q);
  set('labels', state.labels);
  if (state.status.length) params.set('status', state.status.join(','));
  if (state.sort && state.sort !== DEFAULT_SORT) params.set('sort', state.sort);
  if (state.dir !== DEFAULT_DIR) params.set('dir', state.dir);
  // The operations tab only means something on the operations view.
  if (state.section === 'operations' && state.ops !== DEFAULT_OPS_TAB) {
    params.set('ops', state.ops);
  }
  return params;
}
